function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import EventEmitter from 'eventemitter3';
import { log } from '@netless/forge-room';
import { WhiteboardKeys } from './WhiteboardKeys';
import { WhiteboardPermissionFlag } from './WhitePermissions';
import { removeObserver } from '@netless/forge-room';
export class IndexedNavigation extends EventEmitter {
  get idList() {
    return this.pageModel.pageList().filter(id => /^_i_/.test(id));
  }
  get head() {
    return this.ensureValidList().head;
  }
  get lastNodeId() {
    let currentId = this.head;
    let current = this.list[currentId];
    while (current && current.next) {
      currentId = current.next;
      current = this.list[current.next];
    }
    return currentId;
  }
  constructor(pageModel, userMap, indexedPageMap, hasPermission) {
    super();
    _defineProperty(this, "idPool", new Set());
    _defineProperty(this, "pageModel", void 0);
    _defineProperty(this, "userMap", void 0);
    _defineProperty(this, "indexedPageMap", void 0);
    _defineProperty(this, "list", {});
    _defineProperty(this, "hasPermission", void 0);
    _defineProperty(this, "handleIndexedPageMapUpdate", _evt => {
      this.list = this.normalizeList(this.indexedPageMap.get('list')) ?? {};
      this.ensureValidList('update');
      const needRemoveList = this.pageModel.pageList().filter(v => /^_i_/.test(v) && Object.keys(this.list).indexOf(v) < 0);
      const needAddList = Object.keys(this.list).filter(v => this.pageModel.pageList().indexOf(v) < 0);
      this.indexedPageMap.doc.transact(() => {
        needAddList.forEach(id => this.pageModel.addPage(id));
        needRemoveList.forEach(id => this.pageModel.deletePage(id));
      });
    });
    this.hasPermission = hasPermission;
    this.pageModel = pageModel;
    this.userMap = userMap;
    this.indexedPageMap = indexedPageMap;
    this.pageModel.on('switchPage', id => {
      setTimeout(() => {
        const index = this.getPageIndex(id);
        if (index >= 0) {
          this.emit('activePageChange', index);
        }
      });
    });
    this.pageModel.on('pagesChange', () => {
      this.emit('pageCountChange', this.pageCount);
    });
    if (!this.indexedPageMap.has('list')) {
      this.list = {
        '_i_': {
          prev: '',
          next: ''
        }
      };
      this.indexedPageMap.set('list', this.list);
      this.idPool.add('_i_');
    } else {
      this.list = this.normalizeList(this.indexedPageMap.get('list')) ?? {};
      Object.keys(this.list).forEach(id => this.idPool.add(id));
      this.ensureValidList('constructor');
    }
    this.indexedPageMap.observe(this.handleIndexedPageMapUpdate);
  }
  normalizeList(list) {
    if (!list || typeof list !== 'object' || Array.isArray(list)) {
      return null;
    }
    const nextList = {};
    for (const [id, node] of Object.entries(list)) {
      if (!/^_i_/.test(id) || !node || typeof node !== 'object') {
        return null;
      }
      const {
        prev,
        next
      } = node;
      if (typeof prev !== 'string' || typeof next !== 'string') {
        return null;
      }
      nextList[id] = {
        prev,
        next
      };
    }
    return nextList;
  }
  validateList(list) {
    const ids = Object.keys(list);
    if (ids.length === 0) {
      return {
        valid: false,
        reason: 'empty list'
      };
    }
    const heads = ids.filter(id => list[id].prev === '');
    if (heads.length !== 1) {
      return {
        valid: false,
        reason: `expected one head, got ${heads.length}`
      };
    }
    for (const id of ids) {
      const node = list[id];
      if (node.prev && !list[node.prev]) {
        return {
          valid: false,
          reason: `missing prev node ${node.prev}`
        };
      }
      if (node.next && !list[node.next]) {
        return {
          valid: false,
          reason: `missing next node ${node.next}`
        };
      }
      if (node.prev && list[node.prev].next !== id) {
        return {
          valid: false,
          reason: `prev link mismatch for ${id}`
        };
      }
      if (node.next && list[node.next].prev !== id) {
        return {
          valid: false,
          reason: `next link mismatch for ${id}`
        };
      }
    }
    const visited = new Set();
    let currentId = heads[0];
    while (currentId) {
      if (visited.has(currentId)) {
        return {
          valid: false,
          reason: `cycle at ${currentId}`
        };
      }
      visited.add(currentId);
      currentId = list[currentId].next;
    }
    if (visited.size !== ids.length) {
      return {
        valid: false,
        reason: 'detached nodes'
      };
    }
    return {
      valid: true,
      head: heads[0]
    };
  }
  createList(ids) {
    return ids.reduce((list, id, index) => {
      list[id] = {
        prev: ids[index - 1] ?? '',
        next: ids[index + 1] ?? ''
      };
      return list;
    }, {});
  }
  repairList(reason) {
    const pageIds = this.idList;
    const ids = pageIds.length > 0 ? pageIds : Object.keys(this.list).filter(id => /^_i_/.test(id));
    const nextIds = ids.length > 0 ? ids : ['_i_'];
    const nextList = this.createList(nextIds);
    log('indexed navigation recovered', {
      reason,
      pageList: JSON.stringify(this.pageModel.pageList()),
      list: JSON.stringify(this.list),
      nextList: JSON.stringify(nextList)
    }, 'warn');
    const apply = () => {
      for (const id of nextIds) {
        if (this.pageModel.pageList().indexOf(id) < 0) {
          this.pageModel.addPage(id);
        }
      }
      this.list = nextList;
      this.indexedPageMap.set('list', nextList);
    };
    if (this.indexedPageMap.doc) {
      this.indexedPageMap.doc.transact(apply);
    } else {
      apply();
    }
    nextIds.forEach(id => this.idPool.add(id));
    return nextIds[0];
  }
  ensureValidList() {
    let context = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'read';
    const normalized = this.normalizeList(this.list);
    if (!normalized) {
      return {
        head: this.repairList(`${context}: invalid list`)
      };
    }
    this.list = normalized;
    const validation = this.validateList(this.list);
    if (!validation.valid) {
      return {
        head: this.repairList(`${context}: ${validation.reason}`)
      };
    }
    return {
      head: validation.head
    };
  }
  getNextId() {
    const cache = Array.from(this.idPool).filter(id => this.idList.indexOf(id) < 0);
    if (cache.length > 0) {
      return cache[0];
    }
    let tail = Math.random().toString(32).substring(3, 5);
    while (this.idPool.has(`_i_${tail}`)) {
      tail = Math.random().toString(32).substring(3, 5);
    }
    const next = `_i_${tail}`;
    this.idPool.add(next);
    return next;
  }
  initIndexed() {
    return new Promise(resolve => {
      setTimeout(() => {
        this.pageModel.addPage('_i_');
        if (this.pageModel.pageList().length === 0) {
          resolve(true);
        } else {
          resolve(false);
        }
      });
    });
  }
  /**
     * 获取总页数
     * @returns {number}
     */
  get pageCount() {
    return this.idList.length;
  }

  /**
     * 获取当前页索引
     */
  get currentPageIndex() {
    const userPageId = this.userMap.get(WhiteboardKeys.currentPage);
    return this.getPageIndex(userPageId);
  }
  get currentPageId() {
    return this.userMap.get(WhiteboardKeys.currentPage);
  }
  getPageIndex(pageId) {
    let i = 0;
    let currentId = this.head;
    while (currentId !== pageId) {
      const data = this.list[currentId];
      if (data) {
        currentId = data.next;
        i += 1;
      } else {
        return -1;
      }
    }
    return i;
  }

  /**
     * 获取指定索引对应的 pageId
     * @param {number} index
     */
  getPageId(index) {
    const firstKey = this.head;
    if (firstKey) {
      let i = 0;
      let currentId = firstKey;
      while (i < index) {
        i += 1;
        const node = this.list[currentId];
        if (node && node.next) {
          currentId = node.next;
        } else {
          return null;
        }
      }
      return currentId;
    }
    return null;
  }
  modifyDetect() {
    const cameraMode = this.userMap.get(WhiteboardKeys.cameraMode);
    if (cameraMode !== 'main') {
      console.warn('cannot modify pages in non-main view mode.');
      return false;
    }
    if (!this.hasPermission(WhiteboardPermissionFlag.mainView)) {
      console.warn('cannot modify pages without WhiteboardPermissionFlag.mainView.');
      return false;
    }
    return true;
  }

  /**
     * 在尾部插入新页面
     * @param {boolean=} goto 是否跳转到新页面, 默认 true
     * @return 总页数
     */
  pushPage() {
    let goto = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
    this.indexedPageMap.doc.transact(() => {
      const id = this.getNextId();
      if (!this.pageModel.addPage(id)) {
        return;
      }
      if (!this.modifyDetect()) {
        return;
      }
      const lastNodeId = this.lastNodeId;
      this.list[id] = {
        prev: lastNodeId,
        next: ''
      };
      const lastNode = this.list[lastNodeId];
      this.list[lastNodeId] = {
        prev: lastNode.prev,
        next: id
      };
      if (goto) {
        this.pageModel.gotoPage(id);
      }
      this.indexedPageMap.set('list', this.list);
    });
    return this.pageCount;
  }

  /**
     * 在 after 之后插入新页面
     * @param {number} after 在此页之后插入
     * @param {boolean=} goto 是否跳转到新页面, 默认 true
     * @return 总页数
     */
  insertPage(after) {
    let goto = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
    const afterPageId = this.getPageId(after);
    if (!afterPageId) {
      return this.pageCount;
    }
    const cameraMode = this.userMap.get(WhiteboardKeys.cameraMode);
    if (cameraMode !== 'main') {
      console.warn('cannot modify pages in non-main view mode.');
      return this.pageCount;
    }
    const prev = this.list[afterPageId];
    if (!prev) {
      return this.pageCount;
    }
    const next = this.list[prev.next];
    if (!next) {
      return this.pushPage(true);
    }
    const nextPageId = this.getNextId();
    if (!this.pageModel.addPage(nextPageId)) {
      return this.pageCount;
    }
    this.list[nextPageId] = {
      next: prev.next,
      prev: afterPageId
    };
    this.list[afterPageId] = {
      prev: prev.prev,
      next: nextPageId
    };
    this.list[prev.next] = {
      prev: nextPageId,
      next: next.next
    };
    if (this.indexedPageMap.doc) {
      this.indexedPageMap.doc.transact(() => {
        this.indexedPageMap.set('list', this.list);
        if (goto) {
          this.pageModel.gotoPage(nextPageId);
        }
      });
    } else {
      this.indexedPageMap.set('list', this.list);
      if (goto) {
        this.pageModel.gotoPage(nextPageId);
      }
    }
    return this.pageCount;
  }

  /**
     * 跳转至上一页, 并返回跳转后的页面索引.
     */
  prevPage() {
    const currentPageId = this.userMap.get(WhiteboardKeys.currentPage);
    const data = this.list[currentPageId];
    if (!data || data.prev === '') {
      return this.currentPageIndex;
    }
    const nextIndex = this.getPageIndex(data.prev);
    this.pageModel.gotoPage(data.prev);
    return nextIndex;
  }

  /**
     * 跳转至下一页, 并返回跳转后的页面索引.
     */
  nextPage() {
    const currentPageId = this.userMap.get(WhiteboardKeys.currentPage);
    const data = this.list[currentPageId];
    if (!data || data.next === '') {
      return this.currentPageIndex;
    }
    const nextIndex = this.getPageIndex(data.next);
    this.pageModel.gotoPage(data.next);
    return nextIndex;
  }

  /**
     * 删除指定页面
     * @param {number} index
     */
  removePage(index) {
    if (this.pageCount <= 1) {
      return this.pageCount;
    }
    if (!this.modifyDetect()) {
      return this.pageCount;
    }
    const targetId = this.getPageId(index);
    if (targetId) {
      const target = this.list[targetId];
      const prev = this.list[target.prev];
      const next = this.list[target.next];
      const replace = target.next || target.prev;
      delete this.list[targetId];
      if (prev) {
        this.list[target.prev] = {
          prev: prev.prev,
          next: target.next
        };
      }
      if (next) {
        this.list[target.next] = {
          prev: target.prev,
          next: next.next
        };
      }
      if (this.indexedPageMap.doc) {
        this.indexedPageMap.doc.transact(() => {
          this.pageModel.deletePage(targetId, replace);
          this.indexedPageMap.set('list', this.list);
        });
      } else {
        this.pageModel.deletePage(targetId, replace);
        this.indexedPageMap.set('list', this.list);
      }
    }
    return this.pageCount;
  }
  gotoPage(index) {
    const targetId = this.getPageId(index);
    if (targetId) {
      this.pageModel.gotoPage(targetId);
    }
  }
  dispose() {
    removeObserver(this.indexedPageMap, this.handleIndexedPageMapUpdate);
    this.pageModel.off('switchPage');
    this.pageModel.off('pagesChange');
  }
}